import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';
import RepeatIcon from '../../../assets/svgs/repet.svg';
import StopwatchIcon from '../../../assets/svgs/round-timer.svg';
import Skeleton from '../../skeleton';
import { exerciseType as exerciseTypes } from '../../../constants/constants';

const labelStyle = tw`text-[26px] max-lg:text-[16px] text-[#475016] leading-[100%] font-[500] [font-family: Inter, sans-serif]`;
const valueStyle = tw`text-[46px] max-lg:text-[28px] text-[#475016] leading-[100%] font-[600] tabular-nums [font-family: Inter, sans-serif]`;

// Skeleton and loaded states share this geometry so the bar doesn't reflow when
// the socket payload lands. The column is wide enough for the longest label
// ("Repetitions"), and the value row is a fixed height — otherwise the icon in
// the reps column pushes its number off the sets column's baseline.
const columnStyle = tw`flex w-[150px] max-lg:w-[96px] flex-col items-center gap-2`;
const valueRowStyle = tw`flex h-[46px] max-lg:h-[28px] items-center justify-center gap-2`;
const dividerStyle = tw`w-[1px] h-20 max-lg:h-8 shrink-0 bg-[#BDC4C9]`;
// pb accounts for the iOS home indicator / Safari bottom bar, which was
// clipping the labels on iPhone.
const statsRowStyle = tw`flex justify-center gap-40 max-lg:gap-5 items-center p-6 max-lg:py-4 max-lg:px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] max-lg:pb-[max(1rem,env(safe-area-inset-bottom))] bg-[#EFF4D7] max-lg:[@media(orientation:landscape)]:flex-1`;
const wrapperStyle = tw`flex flex-col max-lg:[@media(orientation:landscape)]:h-full max-lg:[@media(orientation:landscape)]:flex-1`;
// Landscape (Figma 2057-14797): the countdown ring and the settings button sit on
// a white row at the top of this column, not over the exercise image. The row is
// part of the column so the green title band starts below it — an overlay would
// have the ring floating on top of the green instead.
const controlsRowStyle = tw`flex shrink-0 items-center justify-end gap-2 bg-white px-3 py-2`;

// Same stripping rule as resolveExerciseReadoutFlags in features/exercise/helpers.
// Kept local rather than imported: that module pulls in @mediapipe/tasks-vision,
// which has no business in the intro bar's module graph.
const normalizeToken = value =>
  String(value || '')
    .replace(/[\s_-]+/g, '')
    .toLowerCase();

const Stat = ({ icon, value, label }) => (
  <div css={columnStyle}>
    <div css={valueRowStyle}>
      {icon && (
        <img
          loading="lazy"
          decoding="async"
          src={icon}
          alt=""
          tw="h-[46px] w-[46px] max-lg:h-5 max-lg:w-5 shrink-0 object-contain"
        />
      )}
      <span css={valueStyle}>{value}</span>
    </div>
    <span css={labelStyle} tw="whitespace-nowrap">
      {label}
    </span>
  </div>
);

const StatSkeleton = () => (
  <div css={columnStyle}>
    <Skeleton skeletonStyle={tw`h-[46px] max-lg:h-[28px] w-full rounded-[8px] bg-[#475016]/20`} />
    <Skeleton skeletonStyle={tw`h-[26px] max-lg:h-[16px] w-full rounded-[6px] bg-[#475016]/10`} />
  </div>
);

const InfoBar = ({
  exerciseName,
  reps,
  sets,
  duration,
  exerciseType,
  isLoading,
  // Landscape only: the ring + settings button, rendered as this column's top row.
  // Portrait and desktop keep them over the image and pass nothing.
  controls = null,
}) => {
  const { t } = useTranslation('exercise');
  // REP_BASED is the only type whose set ends on the rep count, so it is the only
  // one whose intro stat is the rep count — the rest are measured by the clock
  // and show `set_eta_seconds` (a TIMED_HOLD has no reps at all, a TIMED_REPS
  // counts them but still ends on time). A REP_BASED payload that states no reps
  // falls back to the duration rather than rendering an empty stat.
  const typeToken = normalizeToken(exerciseType);
  const isRepBased = typeToken === normalizeToken(exerciseTypes.repBased);
  const showTime = !isRepBased || !reps;
  const icon = showTime ? StopwatchIcon : RepeatIcon;
  const value = showTime ? duration : reps;
  const label = showTime ? t('infoBar.seconds') : t('infoBar.repetitions');

  if (isLoading) {
    return (
      <div css={wrapperStyle}>
        {controls && <div css={controlsRowStyle}>{controls}</div>}
        <div tw="p-6 max-lg:py-4 max-lg:px-5 flex items-center justify-center bg-[#E0E9AF] max-lg:[@media(orientation:landscape)]:flex-1">
          <Skeleton
            skeletonStyle={tw`h-[46px] max-lg:h-[32px] w-2/3 rounded-[8px] bg-[#475016]/20`}
          />
        </div>
        <div css={statsRowStyle}>
          <StatSkeleton />
          <div css={dividerStyle} />
          <StatSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div css={wrapperStyle}>
      {controls && <div css={controlsRowStyle}>{controls}</div>}
      <div tw="p-6 max-lg:py-4 max-lg:px-5 text-center bg-[#E0E9AF] max-lg:[@media(orientation:landscape)]:flex-1 max-lg:[@media(orientation:landscape)]:flex max-lg:[@media(orientation:landscape)]:items-center max-lg:[@media(orientation:landscape)]:justify-center">
        <h2
          tw="text-[46px] max-lg:text-[32px] text-[#475016] leading-[100%] font-[900]"
          style={{ fontFamily: 'Roboto, sans-serif' }}
        >
          {exerciseName}
        </h2>
      </div>
      <div tw="flex justify-center gap-40 max-lg:gap-5 items-center p-6 max-lg:py-4 max-lg:px-5 bg-[#EFF4D7] max-lg:[@media(orientation:landscape)]:flex-1">
        <div tw="flex flex-col items-center gap-1">
          <div tw="flex items-center gap-2">
            <img loading="lazy" decoding="async" src={icon} alt="" tw="max-lg:w-5" />
            <span css={valueStyle}>{value}</span>
          </div>
          <span css={labelStyle}>{label}</span>
        </div>
        <div tw="w-[1px] h-20 max-lg:h-8 bg-[#BDC4C9]" />
        <div tw="flex flex-col items-center gap-1">
          <span css={valueStyle}>{sets}</span>
          <span css={labelStyle}>{t('infoBar.set')}</span>
        </div>
      </div>
    </div>
  );
};

export default InfoBar;
